import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress
} from '@mui/material';
import { CallEnd } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface LeaveConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
}

export const LeaveConfirmationDialog: React.FC<LeaveConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = async () => {
    if (isLoading) return; // Prevent double clicks

    setIsLoading(true);
    try {
      await onConfirm();
    } catch (error) {
      console.error('Error during leave confirmation:', error);
      // Reset loading state on error so user can try again
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (isLoading) return; // Prevent closing while loading
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="leave-room-dialog-title"
      aria-describedby="leave-room-dialog-description"
      disableEscapeKeyDown={isLoading}
    >
      <DialogTitle id="leave-room-dialog-title">
        {t('Leave Room')}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="leave-room-dialog-description">
          {isLoading
            ? t('Leaving room and stopping live stream...')
            : t('Are you sure you want to leave this room? You will be disconnected from the call.')
          }
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          color="primary"
          disabled={isLoading}
        >
          {t('Cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          color="error"
          variant="contained"
          startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <CallEnd />}
          disabled={isLoading}
          autoFocus={!isLoading}
        >
          {isLoading ? t('Leaving...') : t('Leave Room')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
